import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban, FileText, BarChart3, Activity,
  ArrowRight, TrendingUp, AlertTriangle, CheckCircle2, BookOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { getDashboardStats } from "../lib/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardStats()
      .then((res) => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statCards = [
    { label: "Projects", value: stats?.total_projects || 0, icon: FolderKanban, color: "text-[#003057]", bg: "bg-[#003057]/5" },
    { label: "Files Analyzed", value: stats?.total_files || 0, icon: FileText, color: "text-[#266497]", bg: "bg-[#266497]/5" },
    { label: "Reports Generated", value: stats?.total_reports || 0, icon: BarChart3, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Diary Entries", value: stats?.total_diary_entries || 0, icon: BookOpen, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-slate-200 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#003057]" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Automotive test reporting overview
          </p>
        </div>
        <Link to="/projects">
          <Button
            data-testid="new-project-btn"
            className="bg-[#003057] hover:bg-[#002545] text-white rounded-sm"
          >
            New Project <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card
            key={i}
            className={`bg-white border border-slate-200 rounded-sm shadow-sm hover:shadow-md transition-shadow animate-fade-in-up animate-delay-${i}00`}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-bold text-[#003057] mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.bg} p-2.5 rounded-sm`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Projects */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="recent-projects-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-[#003057]">
              Recent Projects
            </CardTitle>
            <Link to="/projects" className="text-xs text-[#266497] hover:underline font-medium">
              View all
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {(!stats?.recent_projects || stats.recent_projects.length === 0) ? (
            <div className="p-8 text-center" data-testid="no-projects-message">
              <FolderKanban className="w-10 h-10 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-slate-500">No projects yet</p>
              <Link to="/projects">
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-[#003057] text-[#003057] rounded-sm"
                  data-testid="create-first-project-btn"
                >
                  Create your first project
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {stats.recent_projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
                  data-testid={`project-link-${project.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#003057] truncate">
                      {project.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {project.ecu && (
                        <span className="text-xs text-slate-500">ECU: {project.ecu}</span>
                      )}
                      {project.asil_level && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-slate-300 text-slate-600">
                          {project.asil_level}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{project.file_count || 0} files</span>
                    <span>{project.report_count || 0} reports</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-base font-semibold text-[#003057]">
              Supported Standards
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-700">Automotive SPICE (ASPICE)</p>
                <p className="text-xs text-slate-500">SWE.4, SWE.5, SWE.6 verification support</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-700">ISO 26262 Functional Safety</p>
                <p className="text-xs text-slate-500">ASIL A-D classification and traceability</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-700">V-Model Development</p>
                <p className="text-xs text-slate-500">Unit, integration, and system test levels</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-base font-semibold text-[#003057]">
              Supported File Formats
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-2">
              {[
                "PDF, DOCX, TXT, MD",
                "XLSX, CSV",
                "LOG, JSON, XML",
                "CAN (ASC, BLF, DBC)",
                "ARXML",
                "Python, C/C++",
                "YAML, INI",
                "PNG, JPG",
              ].map((fmt, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#003057]" />
                  {fmt}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
